import { useEffect, useRef, useState } from 'react'

import type { SelectChangeEvent } from '@mui/material'
import { Input, Select, Typography } from 'components'
import { availableCentersFactoriesFilter } from 'constants/enums'
import statusRascunho from 'constants/statusRascunho'
import { URL_CONST } from 'constants/urls'
import type { Material, SimulateDraft } from 'container/SalesOrder/interfaces'
import { useNavigate, useParams } from 'react-router-dom'

import ItemMaterial from './ItemMaterial'
import * as S from './styles'

type SimulateOrderProps = {
  data: SimulateDraft
}

const SimulateOrder = ({ data }: SimulateOrderProps) => {
  const navigate = useNavigate()
  const { orderNumber } = useParams()
  const [filterFactory, setFilterFactory] = useState('todos')
  const [filterName, setFilterName] = useState('')
  const [searchResults, setSearchResults] = useState<SimulateDraft>(data)

  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSearchResults(data)
    if (!data.materiais.length) {
      navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      return
    }
  }, [data])

  useEffect(() => {
    if (cardRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: cardRef.current.offsetTop,
      })
    }
  }, [cardRef.current])

  const handleFactoryFilter = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string

    setFilterFactory(value)
    filterMaterials(filterName, value)
  }

  const handleNameFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as string

    setFilterName(value)
    filterMaterials(value, filterFactory)
  }

  const compareMaterials = (a: Material, b: Material) => {
    const materialA = a.stat.toUpperCase()
    const materialB = b.stat.toUpperCase()
    if (materialA === statusRascunho.ANSWERED && materialB !== statusRascunho.ANSWERED) {
      return 1
    } else if (materialA === statusRascunho.ANSWERED && materialB === statusRascunho.ANSWERED) {
      return 0
    } else if (materialA !== statusRascunho.ANSWERED && materialB === statusRascunho.ANSWERED) {
      return -1
    } else {
      if (materialA > materialB) {
        return 1
      } else if (materialB > materialA) {
        return -1
      }
    }
    return 0
  }

  const sortMaterials = (materials: Material[]) => {
    return materials.slice().sort(compareMaterials)
  }

  const filterMaterials = (name: string, factory: string) => {
    const filteredMaterials = data.materiais.filter((material) => {
      if (factory !== 'todos' && name !== '') {
        return (
          material.ds_matr.toLowerCase().includes(name.toLowerCase()) &&
          material.cota.cd_cent_matr.toLowerCase().includes(factory.toLowerCase())
        )
      } else if (factory !== 'todos' && name === '') {
        return material.cota.cd_cent_matr.toLowerCase().includes(factory.toLowerCase())
      } else if (factory === 'todos' && name !== '') {
        return (
          material.ds_matr.toLowerCase().includes(name.toLowerCase()) ||
          material.cores.some((cor) => cor.ds_cor.toLowerCase().includes(name.toLowerCase()))
        )
      }

      return true
    })

    setSearchResults((prevState) => ({
      ...prevState,
      materiais: filteredMaterials,
    }))
  }

  return (
    <>
      <S.CardMaterial ref={cardRef}>
        <S.BoxColumn>
          <div className="deliveryHeader">
            <Typography className="materialTitle" design="hav-h5-text">
              Materiais
            </Typography>
            <Select
              label="Fábrica/Centro"
              name="fabrica"
              onChange={(e) => handleFactoryFilter(e)}
              options={availableCentersFactoriesFilter}
              value={filterFactory}
            />
            <div>
              <Input
                InputProps={{ placeholder: 'Digite aqui o material ou cor' }}
                label="Buscar materiais"
                onChange={handleNameFilter}
                value={filterName}
              />
            </div>
          </div>
          <div>
            {sortMaterials(searchResults.materiais).map((material) => (
              <ItemMaterial
                key={`${material.cd_matr}-${material.nr_item_pedi_rasc}`}
                material={material}
              />
            ))}
          </div>
        </S.BoxColumn>
      </S.CardMaterial>
    </>
  )
}

export default SimulateOrder
